import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIngestionRun } from '../../../hooks/use-ingestion-runs'
import { QcReviewPanel } from './qc-review-panel/QcReviewPanel'
import {
  ActionButtons,
  ArtefactSummaryTable,
  ClientInfoGrid,
  IssuesSection,
  PanelStatus,
} from './run-detail-panel/run-detail-panel.parts'

interface RunDetailPanelProps {
  caseId: string
  runId: string
  onVerify: (runId: string) => void
  onFlagCorrupt: (runId: string) => void
  verifying: boolean
  flagging: boolean
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

function Section({ title, children }: SectionProps): React.ReactElement {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
        {title}
      </h4>
      {children}
    </div>
  )
}

function totalIssueCount(
  issues: { counts: { error?: number; warning?: number; info?: number } } | null | undefined
): number {
  if (!issues) return 0
  const { error = 0, warning = 0, info = 0 } = issues.counts
  return error + warning + info
}

export function RunDetailPanel({
  caseId,
  runId,
  onVerify,
  onFlagCorrupt,
  verifying,
  flagging,
}: RunDetailPanelProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { run, issues, loading, error } = useIngestionRun(caseId, runId)
  const [issuesExpanded, setIssuesExpanded] = useState(false)

  if (loading) return <PanelStatus message={t('detail.processing.processing')} variant="loading" />
  if (error || !run) {
    return <PanelStatus message={error ?? t('detail.processing.runDetail')} variant="error" />
  }

  const issueCount = totalIssueCount(issues)

  return (
    <div className="mt-4 space-y-6 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
      <div className="flex items-center justify-between">
        <h3
          className="text-sm font-semibold text-slate-900 dark:text-slate-100"
          style={{ fontFamily: 'var(--font-display, Syne, sans-serif)' }}
        >
          {t('detail.processing.runDetail')}
        </h3>
        <ActionButtons
          run={run}
          onVerify={onVerify}
          onFlagCorrupt={onFlagCorrupt}
          verifying={verifying}
          flagging={flagging}
          t={t}
        />
      </div>
      <Section title={t('detail.processing.clientInfo')}>
        <ClientInfoGrid run={run} t={t} />
      </Section>
      <Section title={t('detail.processing.artefactSummary')}>
        <ArtefactSummaryTable run={run} t={t} />
      </Section>
      <QcReviewPanel caseId={caseId} runId={runId} runStatus={run.status} />
      <IssuesSection
        issues={issues}
        issueCount={issueCount}
        expanded={issuesExpanded}
        onToggle={() => {
          setIssuesExpanded(!issuesExpanded)
        }}
        t={t}
      />
    </div>
  )
}
